import { captureException } from '@sentry/react';

import {
  EmptyDesignConfig,
  FeatureCategories,
} from 'src/modules/designStudio/constants';
import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import { macheteBuildingRef } from 'src/modules/designStudio/machines/building/ref';
import type { Update } from 'src/modules/designStudio/machines/history/types';
import type {
  LegacyDesignConfig,
  RoofStyle,
  TrimStyle,
  WallStyle,
  WindowStyle,
} from 'src/modules/designStudio/types';
import { WindowParametricProperties } from 'src/modules/designStudio/types';

/**
 * This class provides type definitions and util methods to deal with the design sub-system of machete's feature api.
 * This class was introduced as part of the effort to refactor the data format used by design studio to store changes to the model.
 * For details on the refactor please refer to this document:
 * - https://hoverinc.atlassian.net/wiki/spaces/EPD/pages/2887483397/Design+Studio+Data+Format+Refactor+50+80+110
 * This file helps implement the 80% solution laid out in the proposal.
 *
 */

export enum DoorGroup {
  FRONT = 'FRONT',
  SERVICE = 'SERVICE',
  GARAGE = 'GARAGE',
  SLIDING_GLASS_DOOR = 'SLIDING_GLASS_DOOR',
}

export const PRIMARY_TEMPLATE_NAME = 'primary-template';

/**
 * Most of these types are here as a temporary measure to expose the types of the design config used in the design sub-system of the feature api.
 * TODO: Remove and consume types from machete: https://hoverinc.atlassian.net/browse/TDP-5280
 */
export type Structure = {
  designName?: string;
  wallStyles?: {
    name?: string;
    policy: WallStylePolicy;
    appearance: TemplateWallAppearance;
    trims?: {
      name?: string;
      policy: TrimStylePolicy;
      appearance: TrimAppearance;
    }[];
  }[];
  roofStyles?: {
    policy: {
      onAppearances?: TemplateRoofAppearance[];
    };
    appearance: TemplateRoofAppearance;
  }[];
  windowStyles?: {
    policy: {
      onAppearances?: TemplateWindowAppearance[];
    };
    appearance: TemplateWindowAppearance;
  }[];
  trimStyles?: {
    policy: {
      onAppearances?: TemplateTrimAppearance[];
    };
    appearance: TemplateTrimAppearance;
  }[];
  doorStyles?: {
    policy: {
      onLabels?: Array<number>;
      isFacing?: Array<string>;
    };
    appearance: NetworkAppearance;
  }[];
};

export type WallStructureConditions = {
  hasWindows?: boolean;
  hasDoors?: boolean;
  spansMultipleFloors?: boolean;
  topFloor?: boolean;
};

export type PartitionIntersection = {
  usesConstraints: Partition[];
};

export enum PartitionGuideline {
  GABLE_CUT = 'gable_cut',
}

export enum PartitionSide {
  ABOVE = 'above',
  BELOW = 'below',
}

export type Partition = {
  cutAlong: PartitionGuideline;
  keepSide: PartitionSide;
};

export type TrimStylePolicy = {
  onOrientations?: EdgeOrientation[];
  onCorners?: EdgeGeometry[];
  withinFaces?: EdgePosition[];
  onAppearances?: TextureAppearance[];

  /**
   * if onFaces is defined,
   * the trim must lie on a face
   * that has a style using one of the listed names
   */
  onFaces?: string[];

  /**
   * if hasNeighbors is defined,
   * the face neighboring the owner face
   * must have one of the listed Designs applied to it.
   */
  hasNeighbors?: string[];
};

export type TrimAppearance = {
  texture: TextureAppearance;
  width: number;
};

export enum EdgeOrientation {
  HORIZONTAL = 'horizontal',
  SLOPED = 'sloped',
  VERTICAL = 'vertical',
}

export enum EdgeGeometry {
  CONVEX = 'convex',
  CONCAVE = 'concave',
  COPLANAR = 'coplanar',
}

export enum EdgePosition {
  EXTERIOR_TOP = 'exterior_top',
  EXTERIOR_BOTTOM = 'exterior_bottom',
  EXTERIOR_SIDE = 'exterior_side',

  INTERIOR_TOP = 'interior_top',
  INTERIOR_BOTTOM = 'interior_bottom',
  INTERIOR_SIDE = 'interior_side',
}

export enum WallDirection {
  FRONT = 'front',
  BACK = 'back',
  SIDE = 'side',
}

export type WallStylePolicy = {
  onAppearances?: TextureAppearance[];
  onLabels?: number[];
  onSides?: WallDirection[];
  isFacing?: WallDirection[];
  hasPartitions?: PartitionIntersection[];
  hasStructuralConditions?: WallStructureConditions[];
};

export type TextureAppearance = {
  textureId?: number | null;
  color?: string | null;
};

export type NetworkAppearance = {
  url: string;
};

export type TemplateWallAppearance = TextureAppearance;

export type TemplateRoofAppearance = TextureAppearance;

export type TemplateTrimAppearance = {
  width?: number | null;
  texture?: {
    textureId?: number | null;
    color?: string | null;
  };
};

export type TemplateWindowAppearance = {
  basicParametricOptions?: {
    frameType: string | null;
    grilleType: string | null;
  };
  color?: string | null;
};

export type DesignBuilding = {
  designer: Designer;
};

export type Designer = {
  set_active_design: (config: Structure) => Promise<Design>;
  get_active_design: () => Design;
  view_design: (name: string) => void;
};

export type DoorCounts = {
  [DoorGroup.FRONT]: {
    count: number;
  };
  [DoorGroup.SERVICE]: {
    count: number;
  };
  [DoorGroup.GARAGE]: {
    count: number;
  };
  [DoorGroup.SLIDING_GLASS_DOOR]: {
    count: number;
  };
};

export type Design = {
  change_wall_appearances: (
    appearance_before: TemplateWallAppearance[],
    appearance_after: TemplateWallAppearance,
  ) => void;
  get_wall_appearances: () => TemplateWallAppearance[];

  change_roof_appearances: (
    appearance_before: TemplateRoofAppearance[],
    appearance_after: TemplateRoofAppearance,
  ) => void;
  get_roof_appearances: () => TemplateRoofAppearance[];

  change_trim_appearances: (
    appearance_before: TextureAppearance[],
    appearance_after: TextureAppearance,
  ) => void;
  get_trim_appearances: () => TextureAppearance[];

  change_window_appearances: (
    appearance_before: TemplateWindowAppearance[],
    appearance_after: TemplateWindowAppearance,
  ) => void;
  get_window_appearances: () => TemplateWindowAppearance[];

  to_config: () => Structure;
  apply_palette: (palette: TemplatePaletteConfig) => Promise<void>;
  get_door_counts: () => DoorCounts;
};

/**
 * @description returns "empty" template design config. When applied to the building this config will show the original state
 * of the building. This config can be used to reset the appearance of the building to its original at load appearance.
 */
export const createEmptyTemplateDesignConfig = (): Structure => ({
  designName: PRIMARY_TEMPLATE_NAME,
  wallStyles: [],
  roofStyles: [],
  trimStyles: [],
  windowStyles: [],
});

/**
 * @description sets the appearance of the building to its original at load time appearance.
 */
export function resetTemplateDesignConfig(): Structure {
  const emptyTemplateDesignConfig = createEmptyTemplateDesignConfig();
  if (macheteBuildingRef.current) {
    macheteBuildingRef.current.designer
      .set_active_design(emptyTemplateDesignConfig)
      .catch(error => captureException(error));
  }
  return emptyTemplateDesignConfig;
}

/**
 * @description isLegacyDesignConfig returns true if the designConfig is of type LegacyDesignConfig
 * This is useful during the transition of designConfig from LegacyDesignConfig -> Structure
 */
export function isLegacyDesignConfig(
  designConfig: LegacyDesignConfig | Structure | undefined,
): designConfig is LegacyDesignConfig {
  if (!designConfig) {
    return false;
  }

  if (
    (designConfig as LegacyDesignConfig).ROOFS &&
    (designConfig as Structure).roofStyles
  ) {
    throw new Error('Design config is union of LegacyConfig & TemplateConfig.');
  }

  return (designConfig as LegacyDesignConfig).ROOFS !== undefined;
}

/**
 * @description isTemplateDesignConfig returns true if the designConfig is of type Structure
 * This is useful during the transition of designConfig from LegacyDesignConfig -> Structure
 */
export function isTemplateDesignConfig(
  designConfig: LegacyDesignConfig | Structure | undefined,
): designConfig is Structure {
  if (!designConfig) {
    return false;
  }

  if (
    (designConfig as LegacyDesignConfig).ROOFS &&
    (designConfig as Structure).roofStyles
  ) {
    throw new Error('Design config is union of LegacyConfig & TemplateConfig.');
  }

  return (designConfig as LegacyDesignConfig).ROOFS === undefined;
}

/**
 * @description When a user changes the appearance of a category (WALLS, ROOFS, TRIMS, WINDOWS), the new appearance
 * is encoded in the parameters category and update. The design system of the feature api can't interpret the appearance
 * from those parameters, this function returns an appearance object which the design system can understand. This helper
 * function allows us to describe the appearance update in a form understood by the design system of the feature api.
 */
export function createTemplateAppearanceFromUpdate(
  category: DesignConfigCategories,
  update: Update,
):
  | TemplateWindowAppearance
  | TextureAppearance
  | TemplateWallAppearance
  | TemplateRoofAppearance
  | undefined {
  if (category === DesignConfigCategories.ROOF) {
    const roofStyle = update as RoofStyle;
    return {
      textureId: roofStyle.textureId ?? null,
      color: roofStyle.color ?? null,
    };
  }
  if (category === DesignConfigCategories.WALL) {
    const wallStyle = update as WallStyle;
    return {
      textureId: wallStyle.textureId ?? null,
      color: wallStyle.color ?? null,
    };
  }
  if (category === DesignConfigCategories.TRIM) {
    const trimStyle = update as TrimStyle;
    return {
      textureId: trimStyle.textureId ?? null,
      color: trimStyle.color ?? null,
    };
  }
  if (category === DesignConfigCategories.WINDOW) {
    const windowStyle = update;
    const frameType =
      windowStyle[WindowParametricProperties.FRAME_TYPE] ?? 'casement';
    const grilleType =
      windowStyle[WindowParametricProperties.GRID_TYPE] ?? 'none';
    return {
      basicParametricOptions: {
        frameType,
        grilleType,
      },
      color: windowStyle.color ?? null,
    };
  }

  return undefined;
}

/**
 * @description This function converts the legacy design config into the new template format.
 */
export function convertLegacyToTemplate(
  legacyDesignConfig: LegacyDesignConfig,
): Structure {
  return {
    designName: PRIMARY_TEMPLATE_NAME,
    wallStyles: !legacyDesignConfig.WALLS.textureId
      ? []
      : [
          {
            policy: {
              onAppearances: legacyDesignConfig.WALLS.textureId ? [{}] : [],
            },
            appearance: createTemplateAppearanceFromUpdate(
              DesignConfigCategories.WALL,
              legacyDesignConfig[DesignConfigCategories.WALL],
            ) as TemplateWallAppearance,
          },
        ],
    roofStyles: !legacyDesignConfig.ROOFS.textureId
      ? []
      : [
          {
            policy: {
              onAppearances: legacyDesignConfig.ROOFS.textureId ? [{}] : [],
            },
            appearance: createTemplateAppearanceFromUpdate(
              DesignConfigCategories.ROOF,
              legacyDesignConfig[DesignConfigCategories.ROOF],
            ) as TemplateRoofAppearance,
          },
        ],
    trimStyles: !legacyDesignConfig.TRIMS.color
      ? []
      : [
          {
            policy: {
              onAppearances: [{}],
            },
            appearance: {
              width: -1,
              texture: createTemplateAppearanceFromUpdate(
                DesignConfigCategories.TRIM,
                legacyDesignConfig[DesignConfigCategories.TRIM],
              ) as TextureAppearance,
            },
          },
        ],
    windowStyles: !legacyDesignConfig.WINDOWS.frameType
      ? []
      : [
          {
            policy: {
              onAppearances: [{}],
            },
            appearance: createTemplateAppearanceFromUpdate(
              DesignConfigCategories.WINDOW,
              legacyDesignConfig[DesignConfigCategories.WINDOW] as Update,
            ) as TemplateWindowAppearance,
          },
        ],
  };
}

/**
 * @description Converts from the new template design config format to the legacy one.
 *
 * This method is called in the case where a user has saved a design in the template format and the feature flag DS_design-template-config has been turned off.
 * In this case we are going to attempt a data rollback, and save as much state as possible. There are three cases:
 *
 * Note: We cann't use designer.get_wall_appearances() or other methods because we are doing a rollback and dont want to access this part of the api.
 *
 * 1.) The user saved in template format and changed walls but did not use the material groups UI or template UI. This means the user changed
 * all their walls, we can recover this data by accessing wallStyles[0].
 *
 * 2.) The user saved in template format, and used the material groups UI to change a subset of walls.
 * In this case we will throw out wall style information.
 *
 * 3.) The user saved in template format, and used the templates UI to change the model.
 * In this case we will throw out wall style information.
 *
 * Note: This method is lossy. Template config contains data not present in legacy one. This conversion is only valid
 * if all elements in category have same appearance. For example if there are two styles of walls, and this function is invoked
 * it will return null.
 */
export function convertTemplateToLegacy(
  templateConfig: Structure,
): LegacyDesignConfig {
  const allRoofsHaveSameStyle = templateConfig?.roofStyles?.length === 1;
  const allTrimsHaveSameStyle = templateConfig?.trimStyles?.length === 1;
  const allWindowsHaveSameStyle = templateConfig?.windowStyles?.length === 1;

  const roofStyle: RoofStyle = { textureId: null, color: null };
  if (templateConfig.roofStyles && allRoofsHaveSameStyle) {
    const { textureId, color } = templateConfig.roofStyles[0].appearance;
    roofStyle.textureId = textureId ?? null;
    roofStyle.color = color ?? null;
  }

  const trimStyle: TrimStyle = { textureId: null, color: null };
  if (templateConfig.trimStyles && allTrimsHaveSameStyle) {
    const { textureId, color } =
      templateConfig.trimStyles[0].appearance.texture || {};
    trimStyle.textureId = textureId ?? null;
    trimStyle.color = color ?? null;
  }

  const wallStyle: WallStyle = { textureId: null, color: null };
  if (templateConfig.wallStyles && templateConfig.wallStyles.length > 0) {
    const { textureId, color } = templateConfig.wallStyles[0].appearance;
    wallStyle.textureId = textureId ?? null;
    wallStyle.color = color ?? null;
  }

  const windowStyle: WindowStyle = {
    [WindowParametricProperties.FRAME_TYPE]: null,
    [WindowParametricProperties.GRID_TYPE]: null,
    color: null,
  };

  if (templateConfig.windowStyles && allWindowsHaveSameStyle) {
    const { appearance } = templateConfig.windowStyles[0];

    const color = appearance.color ?? '#ffffff';
    const frameType =
      appearance.basicParametricOptions?.frameType ?? 'casement';
    const grilleType = appearance.basicParametricOptions?.grilleType ?? 'none';

    windowStyle[WindowParametricProperties.FRAME_TYPE] = frameType;
    windowStyle[WindowParametricProperties.GRID_TYPE] = grilleType;
    windowStyle[WindowParametricProperties.COLOR] = color;
  }

  const legacyConfig = {
    [FeatureCategories.ROOF]: roofStyle,
    [FeatureCategories.TRIM]: trimStyle,
    [FeatureCategories.WALL]: wallStyle,
    [FeatureCategories.WINDOW]: windowStyle,
  };

  /**
   * designConfigUsesTemplates is a heuristic for whether the designConfig was generated from applying a template.
   */
  const designConfigUsesTemplates =
    templateConfig.designName !== PRIMARY_TEMPLATE_NAME;

  /**
   * We throw out all information if the user applied a template, and we are rollin back.
   */
  if (designConfigUsesTemplates) {
    return EmptyDesignConfig;
  }

  return legacyConfig;
}

/**
 * @description This function is used to update the building in response to the following UI selections:
 *
 * - WALLS: changing texture/color
 * - ROOFS: changing texture/color
 * - TRIMS: changing color
 * - WINDOWS: changing frameType/grilleType/color
 *
 * The category and update parameters are passed in by the components invoking the update, and this method interprets those
 * parameters to then make the appropriate feature api calls.
 *
 * Note: This method actually updates the building's appearance.
 */
export function applyDesignUpdateThroughTemplateSystem(
  designer: Designer,
  category: DesignConfigCategories,
  update: Update,
): void {
  // Machete does not expose designer type yet
  // eslint-disable-next-line
  const active = designer.get_active_design();
  const appearance = createTemplateAppearanceFromUpdate(category, update);

  switch (category) {
    case DesignConfigCategories.ROOF:
      // Machete does not expose designer type yet
      // eslint-disable-next-line
      active.change_roof_appearances(
        [{}],
        appearance as TemplateRoofAppearance,
      );
      break;
    case DesignConfigCategories.WALL:
      // Machete does not expose designer type yet
      // eslint-disable-next-line
      active.change_wall_appearances(
        [{}],
        appearance as TemplateWallAppearance,
      );
      break;
    case DesignConfigCategories.TRIM:
      // Machete does not expose designer type yet
      // eslint-disable-next-line
      active.change_trim_appearances([{}], appearance as TextureAppearance);
      break;
    case DesignConfigCategories.WINDOW:
      // Machete does not expose designer type yet
      // eslint-disable-next-line
      active.change_window_appearances(
        [{}],
        appearance as TemplateWindowAppearance,
      );
      break;
    default:
  }
}

export function getDesignConfigInDesiredFormat(
  config: LegacyDesignConfig | Structure | undefined,
  desiredFormat: 'LEGACY' | 'TEMPLATE',
): LegacyDesignConfig | Structure | undefined {
  if (config === undefined) {
    return desiredFormat === 'LEGACY'
      ? EmptyDesignConfig
      : createEmptyTemplateDesignConfig();
  }
  if (isTemplateDesignConfig(config)) {
    return desiredFormat === 'LEGACY'
      ? convertTemplateToLegacy(config)
      : config;
  }
  return desiredFormat === 'LEGACY' ? config : convertLegacyToTemplate(config);
}

/**
 * @description This function is only usefull in the context of providing components the legacy design config for UI purposes.
 * This should not be used to actually set the design config in the history machine.
 *
 * Note: This function uses the methods exposed on Design to create a legacy design config. This is NOT the same as
 * converting the template config to the legacy config.
 */
export function getActiveAppearanceAsLegacyConfig(
  building: DesignBuilding,
): LegacyDesignConfig {
  const roofAppearances = building.designer
    .get_active_design()
    .get_roof_appearances();
  const trimAppearances = building.designer
    .get_active_design()
    .get_trim_appearances();
  const wallAppearances = building.designer
    .get_active_design()
    .get_wall_appearances();
  const windowAppearances = building.designer
    .get_active_design()
    .get_window_appearances();

  let roofAppearance: RoofStyle = { textureId: null, color: null };
  let trimAppearance: TrimStyle = { textureId: null, color: null };
  let wallAppearance: WallStyle = { textureId: null, color: null };
  let windowAppearance: WindowStyle = {
    [WindowParametricProperties.FRAME_TYPE]: null,
    [WindowParametricProperties.GRID_TYPE]: null,
    color: null,
  };

  if (roofAppearances.length === 1) {
    roofAppearance = {
      textureId: roofAppearances[0].textureId ?? null,
      color: roofAppearances[0].color ?? null,
    };
  }

  if (trimAppearances.length === 1) {
    trimAppearance = {
      textureId: trimAppearances[0].textureId ?? null,
      color: trimAppearances[0].color ?? null,
    };
  }

  if (wallAppearances.length === 1) {
    wallAppearance = {
      textureId: wallAppearances[0].textureId ?? null,
      color: wallAppearances[0].color ?? null,
    };
  }

  if (windowAppearances.length === 1) {
    windowAppearance = {
      [WindowParametricProperties.FRAME_TYPE]:
        windowAppearances[0].basicParametricOptions?.frameType ?? null,
      [WindowParametricProperties.GRID_TYPE]:
        windowAppearances[0].basicParametricOptions?.grilleType ?? null,
      color: windowAppearances[0].color ?? null,
    };
  }

  return {
    [FeatureCategories.ROOF]: roofAppearance,
    [FeatureCategories.TRIM]: trimAppearance,
    [FeatureCategories.WALL]: wallAppearance,
    [FeatureCategories.WINDOW]: windowAppearance,
  };
}

/**
 * @description The designConfig stored in the history machine can now be either in legacy or template format.
 * Various components only work with the legacy format, this method solves this issue by always returning the design config in legacy format.
 */
export function getGuardedLegacyConfig(
  designConfig: LegacyDesignConfig | Structure,
): LegacyDesignConfig {
  if (designConfig === undefined) {
    return EmptyDesignConfig;
  }
  if (isTemplateDesignConfig(designConfig)) {
    const building = macheteBuildingRef.current as DesignBuilding;
    if (!building) {
      return EmptyDesignConfig;
    }
    return getActiveAppearanceAsLegacyConfig(building);
  }
  return designConfig;
}

export function changeAppearanceOfWallMaterialGroup(
  designer: Designer,
  category: DesignConfigCategories,
  update: Update,
): void {
  designer
    .get_active_design()
    .change_wall_appearances(
      [update.targetAppearanceGroup!],
      createTemplateAppearanceFromUpdate(
        category,
        update,
      ) as TemplateWallAppearance,
    );
}

export function getCurrentDesignConfig(designer: Designer): Structure {
  const config = designer.get_active_design().to_config();
  /**
   * The main use of this function is to return the template config of the building. This config is saved to firebase
   * and used to save the state of the model. Firebase does not handle undefined values, so we remove all undefined values using
   * JSON.parse(JSON.stringify()).
   */
  return JSON.parse(JSON.stringify(config, undefined, 0)) as Structure;
}
